import { ProjectSubcontractorService } from '@http/clients/reporting-app/project-subcontractor.service';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { formatCurrencyCompact, formatNumbersCompact, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import { WidgetType } from '../../types/report-widget-type.enum';
import {
    marketsEditorConfig,
    opportunitySourceEditorConfig,
    projectDueDateEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    projectStatusEditorConfig
} from './shared/additional-filters';
import { salesMainFilters } from './shared/main-filters';

export const ProjectModuleSubcontractor: IReportConfiguration<ProjectSubcontractorService> = {
    base: {
        type: ReportType.ProjectModuleSubcontractors,
        serviceType: ProjectSubcontractorService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'avg', label: 'Avg' },
                        { name: 'min', label: 'Min' },
                        { name: 'med', label: 'Med' },
                        { name: 'max', label: 'Max' }
                    ],
                    rows: [
                        { name: 'QuoteLumpSum', label: 'Quote Lump Sum' },
                        { name: 'QuoteUnitPricing', label: 'Quote Unit Pricing', format: 'currency' },
                        { name: 'DirectCostLumpSum', label: 'DC ($) Lump Sum', format: 'currency' },
                        { name: 'DirectCostUnitPricing', label: 'DC ($) Unit Pricing', format: 'currency' },
                        { name: 'PercentageDirectCostLumpSum', label: 'TDC (%) Lump Sum', format: 'percent' },
                        {
                            name: 'PercentageTotalDirectCostUnitPricing',
                            label: 'TDC (%) Unit Pricing',
                            format: 'percent'
                        }
                    ]
                }
            },
            {
                methodName: 'getLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Subcontractors by Total Sell Price'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'subcontractorName',
                            yKey: 'totalSellPrice',
                            yName: 'Total Sell Price'
                        },
                        {
                            type: 'line',
                            xKey: 'subcontractorName',
                            yKey: 'totalSellPriceLumpSum',
                            yName: 'Total Sell Price Lump Sum'
                        },
                        {
                            type: 'line',
                            xKey: 'subcontractorName',
                            yKey: 'totalSellPriceUnitPricing',
                            yName: 'Total Sell Price Unit Pricing'
                        },
                        {
                            type: 'line',
                            xKey: 'subcontractorName',
                            yKey: 'used',
                            yName: 'Number of Times Used'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['totalSellPrice', 'totalSellPriceLumpSum', 'totalSellPriceUnitPricing'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Quantity'
                            },
                            keys: ['used'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'totalSellPrice',
                            yName: 'Total Sell Price'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'totalSellPriceLumpSum',
                            yName: 'Total Sell Price Lump Sum'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'totalSellPriceUnitPricing',
                            yName: 'Total Sell Price Unit Pricing'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['totalSellPrice', 'totalSellPriceLumpSum', 'totalSellPriceUnitPricing'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.FourRowStack
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    {
                        field: 'subcontractorName',
                        headerName: 'Subcontractor Name',
                        enableRowGroup: true,
                        lockVisible: true
                    },
                    { field: 'totalSellPrice', headerName: 'Total Sell Price', type: 'currency', aggFunc: 'sum' },
                    { field: 'used', headerName: 'Number of times used', aggFunc: 'sum' },
                    {
                        field: 'avgSellPricePerUsed',
                        headerName: 'Average Sell Price per Usage',
                        type: 'currency',
                        aggFunc: 'avg'
                    },
                    { field: 'qtyFinalEstimates', headerName: 'Number of Selected Final Estimates', aggFunc: 'sum' },
                    {
                        field: 'avgSellPricePerFinalEstimates',
                        headerName: 'Average Sell Price per Selected Final Estimate',
                        type: 'currency'
                    },
                    { field: 'lumpSumQty', headerName: 'Number of times used Lump Sum', aggFunc: 'sum' },
                    { field: 'unitPricingQty', headerName: 'Number of times used Unit Pricing', aggFunc: 'sum' },
                    {
                        field: 'totalSellPriceLumpSum',
                        headerName: 'Total Sell Price Lump Sum',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'totalSellPriceUnitPricing',
                        headerName: 'Total Sell Price Unit Pricing',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    { field: 'avgQuoteLumpSum', headerName: 'Avg Quote Lump Sum', aggFunc: 'avg' },
                    { field: 'minQuoteLumpSum', headerName: 'Min Quote Lump Sum', aggFunc: 'sum' },
                    { field: 'medQuoteLumpSum', headerName: 'Med Quote Lump Sum', aggFunc: 'sum' },
                    { field: 'maxQuoteLumpSum', headerName: 'Max Quote Lump Sum', aggFunc: 'sum' },
                    { field: 'avgQuoteUnitPricing', headerName: 'Avg Quote Unit Pricing', aggFunc: 'avg' },
                    { field: 'minQuoteUnitPricing', headerName: 'Min Quote Unit Pricing', aggFunc: 'sum' },
                    { field: 'medQuoteUnitPricing', headerName: 'Med Quote Unit Pricing', aggFunc: 'sum' },
                    { field: 'maxQuoteUnitPricing', headerName: 'Max Quote Unit Pricing', aggFunc: 'sum' },
                    {
                        field: 'avgDirectCostLumpSum',
                        headerName: 'Avg DC ($) Lump Sum',
                        type: 'currency',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'minDirectCostLumpSum',
                        headerName: 'Min DC ($) Lump Sum',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'medDirectCostLumpSum',
                        headerName: 'Med DC ($) Lump Sum',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'maxDirectCostLumpSum',
                        headerName: 'Max DC ($) Lump Sum',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'avgDirectCostUnitPricing',
                        headerName: 'Avg DC ($) Unit Pricing',
                        type: 'currency',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'minDirectCostUnitPricing',
                        headerName: 'Min DC ($) Unit Pricing',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'medDirectCostUnitPricing',
                        headerName: 'Med DC ($) Unit Pricing',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'maxDirectCostUnitPricing',
                        headerName: 'Max DC ($) Unit Pricing',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'avgPercentageDirectCostLumpSum',
                        headerName: 'Avg DC (%) Lump Sum',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'minPercentageDirectCostLumpSum',
                        headerName: 'Min DC (%) Lump Sum',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'medPercentageDirectCostLumpSum',
                        headerName: 'Med DC (%) Lump Sum',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'maxPercentageDirectCostLumpSum',
                        headerName: 'Max DC (%) Lump Sum',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'avgPercentageDirectCostUnitPricing',
                        headerName: 'Avg DC (%) Unit Pricing',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'minPercentageDirectCostUnitPricing',
                        headerName: 'Min DC (%) Unit Pricing',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'medPercentageDirectCostUnitPricing',
                        headerName: 'Med DC (%) Unit Pricing',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'maxPercentageDirectCostUnitPricing',
                        headerName: 'Max DC (%) Unit Pricing',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'avgPercentageTotalDirectCostLumpSum',
                        headerName: 'Avg TDC (%) Lump Sum',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'minPercentageTotalDirectCostLumpSum',
                        headerName: 'Min TDC (%) Lump Sum',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'medPercentageTotalDirectCostLumpSum',
                        headerName: 'Med TDC (%) Lump Sum',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'maxPercentageTotalDirectCostLumpSum',
                        headerName: 'Max TDC (%) Lump Sum',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'avgPercentageTotalDirectCostUnitPricing',
                        headerName: 'Avg TDC (%) Unit Pricing',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'minPercentageTotalDirectCostUnitPricing',
                        headerName: 'Min TDC (%) Unit Pricing',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'medPercentageTotalDirectCostUnitPricing',
                        headerName: 'Med TDC (%) Unit Pricing',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'maxPercentageTotalDirectCostUnitPricing',
                        headerName: 'Max TDC (%) Unit Pricing',
                        type: 'percentage',
                        aggFunc: 'avg'
                    }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...salesMainFilters],
        additional: [
            projectDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            projectStatusEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig
        ]
    }
};
